/**
 * iter310 — Canonical Sudan phone + WhatsApp utility.
 *
 * Single source of truth used by:
 *   • AddAd form (Post Ad)            ← validation before publish
 *   • EditAd form                      ← validation before save
 *   • AdDetails page                   ← WhatsApp button + tel: link
 *   • ContactSellerButton              ← bottom-sheet contact actions
 *   • Any future seller-contact UI
 *
 * Storage / display: E.164 (`+249XXXXXXXXX`).
 * WhatsApp deep link: `https://wa.me/249XXXXXXXXX` (no `+`, no spaces,
 *   no leading local `0`).
 *
 * Accepted input formats:
 *   - local "09XXXXXXXX"         → "+2499XXXXXXXX"
 *   - local "01XXXXXXXX"         → "+2491XXXXXXXX"
 *   - international "+249XXXXXXXXX"
 *   - international "00249XXXXXXXXX"
 *   - Arabic/Indic digits in any of the above
 *   - Spaces, dashes, parens are stripped
 *
 * NOT validated by telecom (Zain/MTN/Sudani) — Sudan prefixes overlap
 * and shift over time.  Only the country code and overall length are
 * enforced (`+249` + 9 subscriber digits).
 */

/** Convert Arabic-Indic (٠-٩) and Persian/Urdu (۰-۹) digits to ASCII. */
export function arabicToEnglishDigits(input) {
  if (input === null || input === undefined) return '';
  return String(input)
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
}

/**
 * Normalize any Sudan-shaped input into a canonical E.164 number plus
 * a WhatsApp digit string. Returns `{ valid: false, reason }` if the
 * input cannot be interpreted as a Sudan number.
 *
 * Example:
 *   normalizeSudanPhone('0963813537')
 *     → { valid: true, e164: '+249963813537', waDigits: '249963813537',
 *         display: '+249963813537' }
 *
 * @param {unknown} raw
 * @returns {{
 *   valid: boolean,
 *   e164: string,
 *   waDigits: string,
 *   display: string,
 *   reason?: 'empty' | 'not_sudan' | 'too_short' | 'too_long' | 'bad_chars',
 * }}
 */
export function normalizeSudanPhone(raw) {
  const empty = { valid: false, e164: '', waDigits: '', display: '' };
  if (raw === null || raw === undefined) return { ...empty, reason: 'empty' };
  let s = arabicToEnglishDigits(String(raw)).trim();
  if (!s) return { ...empty, reason: 'empty' };

  // Drop everything that is not a digit or a leading plus.
  const hadPlus = s.startsWith('+');
  s = s.replace(/[^\d]/g, '');
  if (!s) return { ...empty, reason: 'bad_chars' };

  // Resolve international prefixes → bare country-code form.
  let digits = s;
  if (hadPlus && digits.startsWith('249')) {
    // "+249XXXXXXXXX" — already in international form.
  } else if (digits.startsWith('00249')) {
    digits = digits.slice(2); // "00249..." → "249..."
  } else if (digits.startsWith('249')) {
    // Bare "249..." (no plus, no double-zero) — accept as international.
  } else if (digits.startsWith('0')) {
    // Local Sudan format: "09..." / "01...". Strip the trunk "0".
    digits = '249' + digits.slice(1);
  } else {
    // No recognizable Sudan shape.
    return { ...empty, reason: 'not_sudan' };
  }

  if (!digits.startsWith('249')) return { ...empty, reason: 'not_sudan' };

  const subscriber = digits.slice(3); // digits after country code
  // Sudan mobile / landline subscriber numbers are 9 digits.
  if (subscriber.length < 9) return { ...empty, reason: 'too_short' };
  if (subscriber.length > 9) return { ...empty, reason: 'too_long' };

  const e164 = `+${digits}`;
  return {
    valid: true,
    e164,
    waDigits: digits,         // "249XXXXXXXXX" — wa.me expects no plus
    display: e164,
  };
}

/**
 * Convenience: build a wa.me deep link. Returns '' if invalid.
 *
 * @param {unknown} raw
 * @param {string} [message]  Optional message to pre-fill in chat.
 */
export function toWhatsAppLink(raw, message = '') {
  const norm = normalizeSudanPhone(raw);
  if (!norm.valid) return '';
  const tail = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${norm.waDigits}${tail}`;
}

/** Convenience: returns true when raw can be normalized to Sudan E.164. */
export function isValidSudanPhone(raw) {
  return normalizeSudanPhone(raw).valid;
}

// -----------------------------------------------------------------------------
// Backwards-compatible exports.  Older callers used these tiny helpers; we
// keep the signatures stable while routing through the canonical
// normalization where possible.  When the input is a Sudan number we return
// the E.164 form; non-Sudan inputs fall through to the prior behavior so
// future international support doesn't regress today.
// -----------------------------------------------------------------------------

/** @deprecated Prefer `normalizeSudanPhone(raw).e164`. */
export function sanitizePhoneForTel(raw) {
  const norm = normalizeSudanPhone(raw);
  if (norm.valid) return norm.e164;
  if (!raw) return '';
  const s = arabicToEnglishDigits(String(raw)).trim();
  const hasPlus = s.startsWith('+');
  const digits = s.replace(/[^\d]/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
}

/** @deprecated Prefer `normalizeSudanPhone(raw).display`. */
export function formatPhoneDisplay(raw) {
  const norm = normalizeSudanPhone(raw);
  if (norm.valid) return norm.display;
  return sanitizePhoneForTel(raw);
}

// -----------------------------------------------------------------------------
// iter314 — Country-neutral E.164 normalization.
//
// Wassla is multi-country (Sudan + neighboring + diaspora). The Sudan
// normalizer above is still the canonical path for local "09..." /
// "+249..." inputs, but the contact-validation UX needs to accept ANY
// E.164 number that carries a country code (e.g. +20 Egypt, +1 US,
// +44 UK). This helper tries Sudan first, then falls back to a
// permissive E.164 check:
//   - must start with + (or 00 → +)
//   - 8 to 15 total digits after the country code
//   - country code is 1-3 digits (per ITU-T E.164)
//
// We deliberately do NOT enforce a country whitelist here: country
// numbering plans change over time and any whitelist would silently
// reject legitimate numbers from regions Wassla quietly grows into.
// Instead we let the seller take responsibility for the leading "+"
// and the country code, which is the same contract WhatsApp itself
// uses on wa.me/<digits>.
// -----------------------------------------------------------------------------

/**
 * Normalize any phone-like input into E.164 (`+<countrycode><subscriber>`).
 *
 * Returns Sudan-normalised result for local-format Sudanese inputs so
 * "09..." auto-prefixes to "+249..."; for any other input the user is
 * required to supply the leading "+" (or "00" which we convert).
 *
 * @param {unknown} raw
 * @returns {{ valid: boolean, e164: string, waDigits: string, display: string, reason?: 'empty'|'invalid' }}
 */
export function normalizeInternationalPhone(raw) {
  // Sudan path keeps its very forgiving local-format support.
  const sudan = normalizeSudanPhone(raw);
  if (sudan.valid) return sudan;

  const empty = { valid: false, e164: '', waDigits: '', display: '' };
  if (raw === null || raw === undefined) return { ...empty, reason: 'empty' };
  let s = arabicToEnglishDigits(String(raw)).trim();
  if (!s) return { ...empty, reason: 'empty' };

  // Strip user-friendly separators (spaces, dashes, parens, dots).
  // Preserve a leading "+" — that's the international marker.
  const hadPlus = s.startsWith('+');
  s = s.replace(/[^\d+]/g, '');
  if (!s) return { ...empty, reason: 'invalid' };

  let digits;
  if (hadPlus || s.startsWith('+')) {
    digits = s.replace(/\+/g, '');
  } else if (s.startsWith('00')) {
    digits = s.slice(2);
  } else {
    // No country prefix → we can't tell which country this is. Bail.
    return { ...empty, reason: 'invalid' };
  }

  if (!digits) return { ...empty, reason: 'invalid' };
  // ITU-T E.164 allows up to 15 digits total. A meaningful international
  // number with a 1-3 digit country code should land in 8-15 digits.
  if (digits.length < 8 || digits.length > 15) {
    return { ...empty, reason: 'invalid' };
  }
  // Country codes start with 1-9; reject leading 0 after the "+".
  if (digits.startsWith('0')) return { ...empty, reason: 'invalid' };

  const e164 = `+${digits}`;
  return { valid: true, e164, waDigits: digits, display: e164 };
}

/**
 * iter314 — Country-neutral validity check used by the Post Ad +
 * Edit Ad forms.
 */
export function isValidContactPhone(raw) {
  return normalizeInternationalPhone(raw).valid;
}

// -----------------------------------------------------------------------------
// iter366 — Country-aware normalization for the Edit Profile form.
// The SELECTED profile country decides the dial code for local-format
// inputs; a stray trunk "0" after the dial code is corrected
// (+2490927... → +249927...), never kept.
// -----------------------------------------------------------------------------
import { COUNTRIES } from '../data/countries';

export function getDialCode(countryCode) {
  const c = COUNTRIES.find((x) => x.code === countryCode);
  return (c && c.dial) || '';
}

export function normalizePhoneForCountry(raw, countryCode) {
  if (raw === null || raw === undefined || !String(raw).trim()) {
    return { ok: true, value: null };
  }
  const str = String(raw).trim();
  const cleaned = arabicToEnglishDigits(str).replace(/[\s\-().]/g, '');
  const dial = getDialCode(countryCode);
  const hasIntlPrefix = cleaned.startsWith('+') || cleaned.startsWith('00');

  if (hasIntlPrefix) {
    const intl = normalizeInternationalPhone(cleaned);
    if (!intl.valid) return { ok: false, value: str };
    let e164 = intl.e164;
    if (dial && e164.startsWith(`${dial}0`)) {
      const fixed = `${dial}${e164.slice(dial.length).replace(/^0+/, '')}`;
      const renorm = normalizeInternationalPhone(fixed);
      if (renorm.valid) e164 = renorm.e164;
    }
    return { ok: true, value: e164 };
  }

  // Local-format input → the selected country's dial code wins.
  const digits = cleaned.replace(/[^\d]/g, '');
  if (!digits) return { ok: false, value: str };
  const effectiveDial = dial || '+249';
  const candidate = `${effectiveDial}${digits.replace(/^0+/, '')}`;
  const norm = normalizeInternationalPhone(candidate);
  return norm.valid ? { ok: true, value: norm.e164 } : { ok: false, value: str };
}

// Messenger is optional: accept m.me / facebook profile URLs or a plain username.
export function isValidMessenger(raw) {
  const s = (raw || '').trim();
  if (!s) return true;
  if (/^(https?:\/\/)?(www\.)?(m\.me|messenger\.com|facebook\.com|fb\.com)\/[A-Za-z0-9._\-/?=&%]+$/i.test(s)) return true;
  return /^[A-Za-z0-9._-]{3,50}$/.test(s);
}
